import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X, SlidersHorizontal, Search, ChevronDown, Grid3X3, LayoutList } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import ProductCard from '../components/ProductCard';
import CouponBanner from '../components/CouponBanner';
import { useTheme } from '../context/ThemeContext';
import type { Brand, FlavorCategory } from '../types';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import { brandBySlug, brandByName } from '@shared/brands';
import { flavourBySlug, flavourByName } from '@shared/flavours';
import { strengthBySlug, strengths as allStrengthInfos } from '@shared/strengths';

const allBrands: Brand[] = ['ZYN', 'VELO', 'VELO Shift', 'Pablo', 'White Fox', 'CLEW'];
const allCategories: FlavorCategory[] = ['Mint', 'Fruity', 'Citrus', 'Berry', 'Tropical', 'Other'];
const sortOptions = [
  { value: 'popularity', label: 'Popularity' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'strength', label: 'Strength' },
  { value: 'name', label: 'Name A-Z' },
];

export default function StorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, isLoading } = useProducts();
  const location = useLocation();
  const navigate = useNavigate();

  // Detect which category route we're on: /brand/:slug, /flavour/:slug,
  // /strength/:slug, or plain /store. The matched slug param is the
  // same param name (:slug) across all three routes; we use the path
  // prefix to know which catalogue to look it up in.
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const brandFromRoute = location.pathname.startsWith('/brand/') && routeSlug
    ? brandBySlug[routeSlug]
    : undefined;
  const flavourFromRoute = location.pathname.startsWith('/flavour/') && routeSlug
    ? flavourBySlug[routeSlug]
    : undefined;
  const strengthFromRoute = location.pathname.startsWith('/strength/') && routeSlug
    ? strengthBySlug[routeSlug]
    : undefined;

  const [selectedBrands, setSelectedBrands] = useState<Brand[]>(() => {
    if (brandFromRoute) return [brandFromRoute.name];
    const b = searchParams.get('brand');
    return b ? [b as Brand] : [];
  });

  // Sync brand filter when URL search params or route param change.
  useEffect(() => {
    if (brandFromRoute) {
      setSelectedBrands([brandFromRoute.name]);
      return;
    }
    const b = searchParams.get('brand');
    setSelectedBrands(b ? [b as Brand] : []);
  }, [searchParams, brandFromRoute]);

  const [selectedCategories, setSelectedCategories] = useState<FlavorCategory[]>(() =>
    flavourFromRoute ? [flavourFromRoute.name as FlavorCategory] : []
  );
  const [selectedStrengths, setSelectedStrengths] = useState<number[]>(() =>
    strengthFromRoute ? [strengthFromRoute.value] : []
  );

  // Sync flavour/strength filters when the route changes.
  useEffect(() => {
    setSelectedCategories(flavourFromRoute ? [flavourFromRoute.name as FlavorCategory] : []);
  }, [flavourFromRoute]);
  useEffect(() => {
    setSelectedStrengths(strengthFromRoute ? [strengthFromRoute.value] : []);
  }, [strengthFromRoute]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('popularity');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [gridCols, setGridCols] = useState(3);

  // Filter clicks navigate to dedicated indexable URLs (/brand/:slug,
  // /flavour/:slug, /strength/:slug) so each filter has its own
  // crawlable landing page with unique meta, H1 and intro copy.
  // Clicking the same filter twice while on its dedicated page sends
  // the user back to /store (clears the filter).
  const toggleBrand = (brand: Brand) => {
    const info = brandByName[brand];
    if (brandFromRoute && brandFromRoute.name === brand) {
      navigate('/store');
      return;
    }
    if (info) {
      navigate(`/brand/${info.slug}`);
      return;
    }
    // Fallback: brand not in catalogue, just toggle state.
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const toggleCategory = (cat: FlavorCategory) => {
    const info = flavourByName[cat];
    if (flavourFromRoute && flavourFromRoute.name === cat) {
      navigate('/store');
      return;
    }
    if (info) {
      navigate(`/flavour/${info.slug}`);
      return;
    }
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const toggleStrength = (strength: number) => {
    const slug = `${String(strength).replace('.', '-')}mg`;
    const info = allStrengthInfos.find((s) => s.value === strength);
    if (strengthFromRoute && strengthFromRoute.value === strength) {
      navigate('/store');
      return;
    }
    if (info) {
      navigate(`/strength/${slug}`);
      return;
    }
    setSelectedStrengths((prev) =>
      prev.includes(strength) ? prev.filter((s) => s !== strength) : [...prev, strength]
    );
  };

  // Dynamically derive unique strengths from products
  const availableStrengths = useMemo(() => {
    const strengths = [...new Set(products.map((p) => p.strength))];
    return strengths.sort((a, b) => a - b);
  }, [products]);

  const clearFilters = () => {
    // If we're on a locked category route, navigating to /store clears
    // the URL-bound filter; otherwise just reset in-memory state.
    if (brandFromRoute || flavourFromRoute || strengthFromRoute) {
      navigate('/store');
      return;
    }
    setSelectedBrands([]);
    setSelectedCategories([]);
    setSelectedStrengths([]);
    setInStockOnly(false);
    setSearchQuery('');
    setSearchParams({});
  };

  const filtered = useMemo(() => {
    let result = [...products];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          p.flavor.toLowerCase().includes(q)
      );
    }

    if (selectedBrands.length > 0) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }

    if (selectedCategories.length > 0) {
      result = result.filter((p) => selectedCategories.includes(p.category));
    }

    if (selectedStrengths.length > 0) {
      result = result.filter((p) => selectedStrengths.includes(p.strength));
    }

    if (inStockOnly) {
      result = result.filter((p) => p.inStock);
    }

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'strength':
        result.sort((a, b) => b.strength - a.strength);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        result.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
    }

    // Push sold-out items to the end while preserving sort within each group
    const inStockItems = result.filter((p) => p.inStock);
    const outOfStockItems = result.filter((p) => !p.inStock);
    return [...inStockItems, ...outOfStockItems];
  }, [products, searchQuery, selectedBrands, selectedCategories, selectedStrengths, inStockOnly, sortBy]);

  const activeFilterCount =
    selectedBrands.length + selectedCategories.length + selectedStrengths.length + (inStockOnly ? 1 : 0);
  const { isDark } = useTheme();

  // Resolve which "category route" we're on (brand, flavour, strength, or
  // none — plain /store). Drives H1, intro paragraph, breadcrumb,
  // canonical, SEO meta and JSON-LD names from a single computed object
  // so the rendering code below stays linear instead of nested ternaries.
  type CategoryContext = {
    h1: string;
    intro: string;
    seoTitle: string;
    seoDescription: string;
    canonical: string;
    breadcrumbName: string;
    breadcrumbHref: string;
    itemListName: string;
  };
  const category: CategoryContext | null = brandFromRoute
    ? {
        h1: `${brandFromRoute.name} Nicotine Pouches`,
        intro: brandFromRoute.description,
        seoTitle: brandFromRoute.seoTitle ?? `${brandFromRoute.name} Nicotine Pouches`,
        seoDescription: brandFromRoute.seoDescription ?? brandFromRoute.description,
        canonical: `/brand/${brandFromRoute.slug}`,
        breadcrumbName: brandFromRoute.name,
        breadcrumbHref: `/brand/${brandFromRoute.slug}`,
        itemListName: `${brandFromRoute.name} Nicotine Pouches`,
      }
    : flavourFromRoute
    ? {
        h1: `${flavourFromRoute.name} Nicotine Pouches`,
        intro: flavourFromRoute.description,
        seoTitle: flavourFromRoute.seoTitle,
        seoDescription: flavourFromRoute.seoDescription,
        canonical: `/flavour/${flavourFromRoute.slug}`,
        breadcrumbName: `${flavourFromRoute.name} Flavour`,
        breadcrumbHref: `/flavour/${flavourFromRoute.slug}`,
        itemListName: `${flavourFromRoute.name} Nicotine Pouches`,
      }
    : strengthFromRoute
    ? {
        h1: `${strengthFromRoute.label} Nicotine Pouches`,
        intro: strengthFromRoute.description,
        seoTitle: strengthFromRoute.seoTitle,
        seoDescription: strengthFromRoute.seoDescription,
        canonical: `/strength/${strengthFromRoute.slug}`,
        breadcrumbName: strengthFromRoute.label,
        breadcrumbHref: `/strength/${strengthFromRoute.slug}`,
        itemListName: `${strengthFromRoute.label} Nicotine Pouches`,
      }
    : null;

  // ItemList JSON-LD listing every product on the page.
  const SITE_URL = 'https://zerosmoke.in';
  const itemListJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: category?.itemListName ?? 'ZeroSmoke Store',
    numberOfItems: filtered.length,
    itemListElement: filtered.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Product',
        name: p.name,
        url: `${SITE_URL}/product/${p.id}`,
        image: p.image ? `${SITE_URL}${p.image}` : undefined,
        brand: { '@type': 'Brand', name: p.brand },
        offers: {
          '@type': 'Offer',
          price: p.price.toString(),
          priceCurrency: 'INR',
          availability: p.inStock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: `${SITE_URL}/product/${p.id}`,
        },
      },
    })),
  }), [filtered, category]);

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={category?.seoTitle ?? pageSeoMeta.store.title}
        description={category?.seoDescription ?? pageSeoMeta.store.description}
        canonical={category?.canonical ?? pageSeoMeta.store.canonical}
        jsonLd={[
          buildBreadcrumbJsonLd(
            category
              ? [
                  { name: 'Home', url: '/' },
                  { name: 'Store', url: '/store' },
                  { name: category.breadcrumbName, url: category.breadcrumbHref },
                ]
              : [
                  { name: 'Home', url: '/' },
                  { name: 'Store', url: '/store' },
                ]
          ),
          itemListJsonLd,
        ]}
      />

      <Breadcrumb
        items={
          category
            ? [
                { name: 'Home', href: '/' },
                { name: 'Store', href: '/store' },
                { name: category.breadcrumbName },
              ]
            : [{ name: 'Home', href: '/' }, { name: 'Store' }]
        }
      />
      {/* Header */}
      <div className={`border-b transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className={`text-3xl sm:text-4xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {category?.h1 ?? 'Store'}
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {category?.intro ?? 'Browse our complete range of premium nicotine pouches'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Coupon Banner */}
        <CouponBanner />

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products, brands, flavors..."
              className={`w-full pl-12 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200 bg-white'}`}
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-medium transition-all ${
              showFilters || activeFilterCount > 0
                ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10'
                : isDark ? 'border-gray-700 text-gray-300 bg-gray-900 hover:border-gray-600' : 'border-gray-200 text-gray-700 bg-white hover:border-gray-300'
            }`}
          >
            <SlidersHorizontal size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`appearance-none w-full sm:w-48 px-4 py-3 pr-10 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 ${isDark ? 'bg-gray-900 border-gray-700 text-gray-300' : 'border-gray-200 bg-white text-gray-700'}`}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Grid toggle */}
          <div className={`hidden lg:flex items-center gap-1 rounded-xl border p-1 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <button
              onClick={() => setGridCols(3)}
              className={`p-2 rounded-lg transition-colors ${gridCols === 3 ? 'bg-emerald-500/10 text-emerald-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => setGridCols(4)}
              className={`p-2 rounded-lg transition-colors ${gridCols === 4 ? 'bg-emerald-500/10 text-emerald-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <LayoutList size={16} />
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-8"
            >
              <div className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Filter Products</h3>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                    >
                      <X size={14} /> Clear All
                    </button>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Brands */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Brand</h4>
                    <div className="space-y-2">
                      {allBrands.map((brand) => (
                        <label key={brand} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedBrands.includes(brand)}
                            onChange={() => toggleBrand(brand)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{brand}</span>
                          <span className={`text-xs ml-auto ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                            ({products.filter((p) => p.brand === brand).length})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Categories */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Flavor</h4>
                    <div className="space-y-2">
                      {allCategories.map((cat) => (
                        <label key={cat} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(cat)}
                            onChange={() => toggleCategory(cat)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{cat}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Stock */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Availability</h4>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={inStockOnly}
                        onChange={() => setInStockOnly(!inStockOnly)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>In Stock Only</span>
                    </label>
                  </div>

                  {/* Strength */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Strength</h4>
                    <div className="space-y-2">
                      {availableStrengths.map((strength) => (
                        <label key={strength} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedStrengths.includes(strength)}
                            onChange={() => toggleStrength(strength)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{strength} mg/g</span>
                          <span className={`text-xs ml-auto ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                            ({products.filter((p) => p.strength === strength).length})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedBrands.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium"
              >
                {b}
                <button onClick={() => toggleBrand(b)}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {selectedCategories.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
              >
                {c}
                <button onClick={() => toggleCategory(c)}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {selectedStrengths.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-xs font-medium"
              >
                {s} mg/g
                <button onClick={() => toggleStrength(s)}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {inStockOnly && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                In Stock
                <button onClick={() => setInStockOnly(false)}>
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Showing <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{filtered.length}</span>{' '}
            {filtered.length === 1 ? 'product' : 'products'}
          </p>
        </div>

        {/* Product Grid */}
        {filtered.length > 0 ? (
          <div
            className={`grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-6 ${
              gridCols === 4
                ? 'lg:grid-cols-4'
                : 'lg:grid-cols-3'
            }`}
          >
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Filter size={48} className={`mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No products found</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Try adjusting your filters or search query.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
